import { useEffect, useRef } from 'react'
import { useIsMiniDragging, useIsMiniMenuOpen, useSetMiniContentHovered } from '../../atoms'

const CONTENT_HOVER_DELAY_MS = 70

interface MiniContentHoverOptions {
  /** How long the pointer must stay away before the hover ends, so a quick re-enter doesn't replay a collapse. */
  leaveDelayMs?: number
  /** Runs as the pointer enters, before the content hover starts. */
  onEnter?: () => void
  /** Runs when the hover ends, after `leaveDelayMs`. */
  onLeave?: () => void
}

interface MiniContentHoverHandlers {
  onMouseEnter: () => void
  onMouseLeave: () => void
}

/** Debounced content-hover tracking shared by the pill content and the notched shell. */
export function useMiniContentHover({
  leaveDelayMs = 0,
  onEnter,
  onLeave,
}: MiniContentHoverOptions = {}): MiniContentHoverHandlers {
  const isDragging = useIsMiniDragging()
  const isMenuOpen = useIsMiniMenuOpen()
  const setContentHovered = useSetMiniContentHovered()
  // Entering cancels a pending leave, and leaving cancels a pending enter
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelPending = () => {
    if (pendingRef.current) {
      clearTimeout(pendingRef.current)
    }
    pendingRef.current = null
  }

  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        clearTimeout(pendingRef.current)
      }
    }
  }, [])

  const endHover = () => {
    pendingRef.current = null
    onLeave?.()
    if (!isMenuOpen) {
      setContentHovered(false)
    }
  }

  return {
    onMouseEnter: () => {
      cancelPending()
      onEnter?.()
      if (!isDragging) {
        pendingRef.current = setTimeout(() => setContentHovered(true), CONTENT_HOVER_DELAY_MS)
      }
    },
    onMouseLeave: () => {
      cancelPending()
      if (leaveDelayMs === 0) {
        endHover()
        return
      }
      pendingRef.current = setTimeout(endHover, leaveDelayMs)
    },
  }
}
